import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageContainer, PageHeader, SecondaryButton, PrimaryButton, EmptyState } from "@/components/ui-kit";
import CustomerForm from "@/components/CustomerForm";
import { toast } from "sonner";
import {
  Inbox, Mail, Phone, Building2, MessageCircle, Search, Check, Send,
  CircleDot, UserCheck, XCircle, Trash2, ChevronRight, Download,
  UserPlus, FileText, AlertTriangle,
} from "lucide-react";

const STATUS_META = {
  new:       { label: "Nuevo",        color: "#FF5A00", icon: CircleDot },
  emailed:   { label: "Nuevo",        color: "#FF5A00", icon: CircleDot },
  contacted: { label: "Contactado",   color: "#1976D2", icon: Send },
  converted: { label: "Convertido",   color: "#00C853", icon: UserCheck },
  closed:    { label: "Cerrado",      color: "#757575", icon: XCircle },
  spam:      { label: "Spam",         color: "#B71C1C", icon: XCircle },
};

const STATUS_ORDER = ["new", "contacted", "converted", "closed", "spam"];

function StatusPill({ status, className = "" }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 font-mono-kpi text-[9px] uppercase tracking-[0.18em] border ${className}`}
      style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}0d` }}
    >
      <Icon className="w-2.5 h-2.5" />
      {meta.label}
    </span>
  );
}

function DuplicatesModal({ lead, onSelectExisting, onContinue, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/customers/check-duplicates", {
          params: {
            name: lead.name || "",
            email: lead.email || "",
            rut: lead.rut || "",
          },
        });
        setCandidates(data.candidates || []);
      } catch (e) {
        toast.error("No se pudieron buscar duplicados: " + (e.message || ""));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lead]);

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-lg border-t-4 border-[#FF5A00]" onClick={(e) => e.stopPropagation()} data-testid="duplicates-modal">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-[#FF5A00]" />
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Buscando duplicados</div>
            <div className="font-display font-bold">¿Existe ya este cliente?</div>
          </div>
        </div>
        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-widest">Buscando…</div>
          ) : candidates.length === 0 ? (
            <div className="border border-[#00C853]/30 bg-[#00C853]/5 p-3 text-sm text-[#008C3A]">
              <Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />No se encontraron coincidencias. Puedes crear el cliente sin riesgo.
            </div>
          ) : (
            <>
              <div className="text-xs text-neutral-500">Encontramos {candidates.length} cliente(s) que podrían ser el mismo. Elige uno o continúa igual.</div>
              {candidates.map((cand) => (
                <button
                  key={cand.customer.id}
                  onClick={() => onSelectExisting(cand.customer)}
                  data-testid={`duplicate-candidate-${cand.customer.id}`}
                  className="w-full text-left border border-neutral-200 hover:border-[#FF5A00] p-3 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{cand.customer.name}</div>
                      <div className="text-xs text-neutral-500 font-mono-kpi">{cand.customer.rut || "—"} · {cand.customer.email || "sin email"}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="font-mono-kpi text-[10px] uppercase tracking-wider text-[#FF5A00]">{cand.score >= 100 ? "Muy probable" : cand.score >= 80 ? "Probable" : "Posible"}</div>
                      <div className="text-[10px] text-neutral-400">{cand.reasons.join(" · ")}</div>
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onCancel} data-testid="duplicates-cancel" className="font-mono-kpi text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#0A0A0A]">Cancelar</button>
          <PrimaryButton onClick={onContinue} disabled={loading} data-testid="duplicates-continue">
            Crear cliente nuevo igual
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function LeadDetail({ lead, onClose, onUpdate }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const [showDupes, setShowDupes] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  const setStatus = async (status) => {
    await api.patch(`/leads/${lead.id}`, { status });
    onUpdate();
    toast.success(`Marcado como ${STATUS_META[status]?.label || status}`);
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}`, { notes });
      onUpdate();
      toast.success("Notas guardadas");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este lead? Esta acción no se puede deshacer.")) return;
    await api.delete(`/leads/${lead.id}`);
    toast.success("Lead eliminado");
    onClose();
    onUpdate();
  };

  const startCreateCustomer = () => setShowDupes(true);

  const linkToExisting = async (customer) => {
    try {
      await api.post(`/leads/${lead.id}/link-customer`, { customer_id: customer.id });
      toast.success(`Lead vinculado a ${customer.name}`);
      setShowDupes(false);
      onUpdate();
      navigate(`/admin/clientes/${customer.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const continueCreate = () => {
    setShowDupes(false);
    setShowCustomerForm(true);
  };

  const handleCustomerCreated = async (payload) => {
    const created = await api.post("/customers", payload);
    const newCustomer = created.data;
    // Vincula automáticamente el lead al cliente recién creado.
    let linkWarning = false;
    try {
      await api.post(`/leads/${lead.id}/link-customer`, { customer_id: newCustomer.id });
    } catch (_e) {
      linkWarning = true; // El cliente ya se creó — sólo alertamos que el lead quedó sin vincular.
    }
    if (linkWarning) {
      toast.warning("Cliente creado ✅, pero no se pudo marcar el lead como convertido. Puedes hacerlo manualmente.");
    } else {
      toast.success("Cliente creado y contrato generado ✅");
    }
    onUpdate();
    navigate(`/admin/clientes/${newCustomer.id}`);
  };

  const goCreateQuote = () => {
    if (lead.converted_customer_id) {
      // Ya tiene cliente vinculado — abrir cotizaciones filtradas por él.
      navigate(`/admin/cotizaciones?customer_id=${lead.converted_customer_id}&new=1`);
    } else {
      toast.info("Primero crea el cliente desde este lead para poder cotizar.");
      startCreateCustomer();
    }
  };

  const dateStr = lead.received_at
    ? new Date(lead.received_at).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

  // Pre-fill datos del cliente con la info del lead.
  const customerInitial = showCustomerForm ? {
    name: lead.name || "",
    email: lead.email || "",
    phone: lead.phone || "",
    contact_name: lead.name || "",
    classification: "prospecto",
    notes: `Convertido desde lead recibido ${dateStr}.\nMotivo: ${lead.reason || ""}\n\n${lead.message || ""}`,
    contacts: [{
      name: lead.name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      role: "",
      notify_before_visit: false,
    }],
  } : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white border-l-4 border-[#FF5A00] overflow-y-auto"
        data-testid="lead-detail-panel"
      >
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Lead · {dateStr}
            </div>
            <div className="font-display font-bold text-xl truncate">{lead.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={lead.status} />
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-100"
              data-testid="lead-detail-close"
              aria-label="Cerrar"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact */}
          <div className="grid grid-cols-1 gap-3">
            <a href={`mailto:${lead.email}`} className="flex items-center gap-3 text-sm hover:text-[#FF5A00]">
              <Mail className="w-4 h-4 text-neutral-400" /> {lead.email}
            </a>
            {lead.phone && (
              <a
                href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-3 text-sm hover:text-[#25D366]"
              >
                <MessageCircle className="w-4 h-4 text-neutral-400" /> {lead.phone}
              </a>
            )}
            {lead.company && (
              <div className="flex items-center gap-3 text-sm">
                <Building2 className="w-4 h-4 text-neutral-400" /> {lead.company}
              </div>
            )}
          </div>

          {/* Motivo */}
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Motivo
            </div>
            <div className="border-l-2 border-[#FF5A00] pl-3 text-sm">{lead.reason}</div>
          </div>

          {/* Mensaje */}
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Mensaje
            </div>
            <div className="border border-neutral-200 bg-neutral-50 p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="lead-message">
              {lead.message}
            </div>
          </div>

          {/* Consent */}
          <div className="flex items-center gap-2 text-xs text-neutral-500 border-t border-neutral-100 pt-3">
            {lead.consent ? (
              <><Check className="w-3.5 h-3.5 text-[#00C853]" /> Consintió recibir comunicaciones</>
            ) : (
              <><XCircle className="w-3.5 h-3.5 text-neutral-400" /> Sin consentimiento de comunicaciones</>
            )}
          </div>

          {/* Notas internas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                Notas internas
              </div>
              <button
                onClick={saveNotes}
                disabled={saving}
                data-testid="lead-notes-save"
                className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] hover:text-[#0A0A0A]"
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Ej: llamado el 15/03, sin respuesta. Reintentar el jueves."
              className="w-full border border-neutral-300 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/15 outline-none px-3 py-2 text-sm resize-none"
              data-testid="lead-notes"
            />
            {lead.updated_by && (
              <div className="mt-1 font-mono-kpi text-[9px] uppercase tracking-[0.18em] text-neutral-400">
                Última actualización: {lead.updated_by}
              </div>
            )}
          </div>

          {/* Quick actions — crear cliente / cotización / cambiar estado */}
          <div className="border-t border-neutral-200 pt-5">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">
              Acciones rápidas
            </div>
            {lead.converted_customer_id && (
              <div className="mb-3 border border-[#00C853]/40 bg-[#00C853]/5 px-3 py-2 text-xs" data-testid="lead-converted-info">
                <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-[#00C853]" />
                Vinculado al cliente <a href={`/admin/clientes/${lead.converted_customer_id}`} className="font-bold text-[#008C3A] hover:underline">{lead.converted_customer_name || "cliente"}</a>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <PrimaryButton
                onClick={startCreateCustomer}
                data-testid="lead-create-customer"
              >
                <UserPlus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                {lead.converted_customer_id ? "Crear otro cliente" : "Crear cliente"}
              </PrimaryButton>
              <SecondaryButton onClick={goCreateQuote} data-testid="lead-create-quote">
                <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Crear cotización
              </SecondaryButton>
              {lead.status !== "contacted" && lead.status !== "converted" && (
                <SecondaryButton onClick={() => setStatus("contacted")} data-testid="lead-mark-contacted">
                  <Send className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Marcar como contactado
                </SecondaryButton>
              )}
              {lead.status !== "closed" && (
                <SecondaryButton onClick={() => setStatus("closed")} data-testid="lead-mark-closed">
                  <XCircle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Cerrar
                </SecondaryButton>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="border-t border-neutral-200 pt-5">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">
              Cambiar estado
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  data-testid={`lead-status-${s}`}
                  className={`font-mono-kpi text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                    (lead.status === s || (s === "new" && lead.status === "emailed"))
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "border-neutral-300 hover:border-[#FF5A00] hover:text-[#FF5A00]"
                  }`}
                >
                  {STATUS_META[s].label}
                </button>
              ))}
            </div>
            <button
              onClick={remove}
              data-testid="lead-delete"
              className="mt-6 inline-flex items-center gap-2 font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#B71C1C] hover:text-[#D50000]"
            >
              <Trash2 className="w-3.5 h-3.5" /> Eliminar lead
            </button>
          </div>
        </div>
      </div>
      {showDupes && (
        <DuplicatesModal
          lead={lead}
          onSelectExisting={linkToExisting}
          onContinue={continueCreate}
          onCancel={() => setShowDupes(false)}
        />
      )}
      {showCustomerForm && (
        <CustomerForm
          initial={customerInitial}
          onClose={() => setShowCustomerForm(false)}
          onSubmit={handleCustomerCreated}
        />
      )}
    </div>
  );
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const s = { total: leads.length, new: 0, contacted: 0, converted: 0, closed: 0, spam: 0 };
    for (const l of leads) {
      const key = l.status === "emailed" ? "new" : (l.status || "new");
      if (s[key] !== undefined) s[key] += 1;
    }
    return s;
  }, [leads]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter !== "all") {
        const key = l.status === "emailed" ? "new" : (l.status || "new");
        if (key !== filter) return false;
      }
      if (q) {
        const hay = `${l.name} ${l.email} ${l.company || ""} ${l.message}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [leads, filter, q]);

  const exportCsv = () => {
    const header = ["fecha", "nombre", "email", "telefono", "empresa", "motivo", "estado", "mensaje", "consentimiento"];
    const rows = filtered.map((l) => [
      l.received_at || "",
      l.name || "",
      l.email || "",
      l.phone || "",
      l.company || "",
      l.reason || "",
      STATUS_META[l.status]?.label || l.status || "Nuevo",
      (l.message || "").replace(/\n/g, " "),
      l.consent ? "SI" : "NO",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_allactive_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const chips = [
    { key: "all",       label: "Todos",       count: stats.total, color: "#0A0A0A" },
    { key: "new",       label: "Nuevos",      count: stats.new,       color: "#FF5A00" },
    { key: "contacted", label: "Contactados", count: stats.contacted, color: "#1976D2" },
    { key: "converted", label: "Convertidos", count: stats.converted, color: "#00C853" },
    { key: "closed",    label: "Cerrados",    count: stats.closed,    color: "#757575" },
    { key: "spam",      label: "Spam",        count: stats.spam,      color: "#B71C1C" },
  ];

  return (
    <PageContainer>
      <PageHeader
        kicker={`${stats.total} mensajes · ${stats.new} sin atender`}
        title="Bandeja de leads"
        subtitle="Mensajes recibidos desde el formulario de contacto de allactive.cl. También llegan por email a rodrigoyanez@allactive.cl."
        actions={
          <SecondaryButton onClick={exportCsv} disabled={filtered.length === 0} data-testid="leads-export">
            <Download className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />
            Exportar CSV
          </SecondaryButton>
        }
      />

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`leads-filter-${c.key}`}
              className={`font-mono-kpi text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                filter === c.key
                  ? "text-white border-[#0A0A0A]"
                  : "bg-white border-neutral-300 hover:border-[#FF5A00]"
              }`}
              style={filter === c.key ? { background: c.color } : {}}
            >
              {c.label} · {c.count}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email, empresa…"
            data-testid="leads-search"
            className="pl-9 pr-3 py-2 border border-neutral-300 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/15 outline-none text-sm w-64"
          />
        </div>
      </div>

      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Sin leads por ahora"
          description="Cuando alguien complete el formulario en allactive.cl aparecerá aquí — y también te llegará un correo."
        />
      ) : (
        <div className="border border-neutral-200 bg-white divide-y divide-neutral-100" data-testid="leads-list">
          {filtered.map((l) => {
            const dateStr = l.received_at
              ? new Date(l.received_at).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
              : "—";
            const isNew = l.status === "new" || l.status === "emailed";
            return (
              <button
                key={l.id}
                onClick={() => setSelected(l)}
                data-testid={`lead-row-${l.id}`}
                className="w-full text-left px-5 py-4 hover:bg-neutral-50 flex items-center gap-4 group"
              >
                <div className={`w-1 self-stretch ${isNew ? "bg-[#FF5A00]" : "bg-transparent"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className={`text-sm ${isNew ? "font-bold" : "font-medium"} text-neutral-900 truncate`}>{l.name}</div>
                    <StatusPill status={l.status} />
                    {l.company && (
                      <span className="text-xs text-neutral-500 truncate">· {l.company}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-neutral-500 truncate">
                    <span className="text-[#FF5A00] font-medium mr-2">{l.reason}</span>
                    · {l.message}
                  </div>
                </div>
                <div className="text-xs text-neutral-400 whitespace-nowrap font-mono-kpi tracking-wider">
                  {dateStr}
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-[#FF5A00] transition-colors" />
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <LeadDetail
          lead={selected}
          onClose={() => setSelected(null)}
          onUpdate={async () => { await load(); const { data } = await api.get("/leads"); const fresh = data.find((x) => x.id === selected.id); if (fresh) setSelected(fresh); }}
        />
      )}
    </PageContainer>
  );
}
